import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { accidentAPI, vehicleAPI } from '../api/client';
import { AlertTriangle, Plus, FileText, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

const Accidents = () => {
  const [accidents, setAccidents] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [accidentsRes, vehiclesRes] = await Promise.all([
        accidentAPI.list(),
        vehicleAPI.list()
      ]);
      setAccidents(accidentsRes.data);
      setVehicles(vehiclesRes.data);
    } catch (error) {
      toast.error('Failed to load accidents');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight">Accident Reports</h1>
          <p className="text-muted-foreground mt-1">Manage your accident evidence and reports</p>
        </div>
        <Button asChild data-testid="create-accident-button">
          <Link to="/app/accidents/new">
            <Plus className="h-4 w-4 mr-2" />
            Report Accident
          </Link>
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-muted/30">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <div className="bg-primary/10 p-2 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h3 className="font-semibold mb-2">Comprehensive Accident Evidence Collection</h3>
              <p className="text-sm text-muted-foreground">
                Our guided workflow helps you capture all necessary evidence at the scene:
                photos from multiple angles, video recording, witness details, and your statement.
                Generate professional PDF reports ready for insurance claims.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accident List */}
      {accidents.length > 0 ? (
        <div className="grid gap-4">
          {accidents.map((accident) => {
            const vehicle = vehicles.find(v => v.vehicle_id === accident.vehicle_id);
            return (
              <Link key={accident.report_id} to={`/app/accidents/${accident.report_id}`}>
                <Card className="card-elevated hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className="bg-destructive/10 p-3 rounded-lg">
                          <AlertTriangle className="h-6 w-6 text-destructive" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-semibold text-lg">{accident.report_id}</h3>
                            {accident.pdf_url && (
                              <Badge variant="outline" className="text-xs">
                                <FileText className="h-3 w-3 mr-1" />
                                PDF Ready
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-1">
                            {format(parseISO(accident.accident_date), 'dd MMMM yyyy')} at {accident.accident_time}
                          </p>
                          <p className="text-sm text-muted-foreground mb-2">
                            📍 {accident.location}
                          </p>
                          {vehicle && (
                            <p className="text-sm">
                              Vehicle: {vehicle.year} {vehicle.make} {vehicle.model} ({vehicle.reg_plate})
                            </p>
                          )}
                          <div className="flex items-center gap-4 mt-3 text-sm text-muted-foreground">
                            {accident.photos && (
                              <span>📷 {accident.photos.length} photos</span>
                            )}
                            {accident.video_url && (
                              <span>🎥 Video recorded</span>
                            )}
                            {accident.witnesses && accident.witnesses.length > 0 && (
                              <span>👥 {accident.witnesses.length} witnesses</span>
                            )}
                          </div>
                        </div>
                      </div>
                      {accident.pdf_url && (
                        <Button variant="outline" size="sm" onClick={(e) => {
                          e.preventDefault();
                          // Download PDF logic
                        }}>
                          <Download className="h-4 w-4 mr-2" />
                          PDF
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-16 text-center">
            <AlertTriangle className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">No Accident Reports</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              Hopefully you'll never need this, but if an accident occurs, our guided workflow
              will help you collect all necessary evidence for your insurance claim.
            </p>
            <Button asChild>
              <Link to="/app/accidents/new">
                <Plus className="h-4 w-4 mr-2" />
                Create First Report
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Accidents;
